import type { MaintenanceTarget, OperationCommand } from '../types/cfdWorkspace'
import type { TradingOperation } from '../types/operations'

export interface MaintenanceValues {
  action: 'reduce' | 'full_close' | 'adjust' | 'cancel'
  percentage?: number
  breakeven?: boolean
  slMode?: 'keep' | 'entry' | 'price' | 'clear'; slPrice?: number
  tpMode?: 'keep' | 'price' | 'clear'; tpPrice?: number
}

export const newRequestId = () => `ui-${typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('')}`

/** Build each intent once, before send. No UI percentages are treated as fills. */
export function maintenanceCommands(target: MaintenanceTarget, values: MaintenanceValues, baseId: string): OperationCommand[] {
  const selector = target.position_id ? { position_id: target.position_id }
    : target.signal_id ? { signal_id: target.signal_id } : {}
  const base = { operation_contract_version: 2 as const, ...selector }
  if (values.action === 'cancel') {
    if (!target.order_id) throw new Error('缺少明确的订单 ID')
    return [{ operation_contract_version: 2, request_id: baseId, operation: 'cancel', order_id: target.order_id }]
  }
  if (!target.position_id && !target.signal_id) throw new Error('缺少明确的仓组或仓位 ID')
  if (values.action === 'full_close') return [{ ...base, request_id: baseId, operation: 'full_close' }]
  if (values.action === 'reduce') {
    if (!values.percentage || values.percentage <= 0 || values.percentage > 100) throw new Error('减仓比例须在 0–100% 之间')
    const reduce: OperationCommand = { ...base, request_id: `${baseId}-reduce`, operation: 'reduce', close_percentage: values.percentage }
    return values.breakeven ? [reduce, { ...base, request_id: `${baseId}-be`, operation: 'adjust',
      sl_to_entry: true, ordering_after_request_id: reduce.request_id }] : [reduce]
  }
  const command: OperationCommand = { ...base, request_id: baseId, operation: 'adjust' }
  if (values.slMode === 'entry') command.sl_to_entry = true
  if (values.slMode === 'clear') command.sl_clear = true
  if (values.slMode === 'price') {
    if (!values.slPrice || values.slPrice <= 0) throw new Error('请输入有效止损价')
    command.sl_price = values.slPrice
  }
  if (values.tpMode === 'clear') command.tp_clear = true
  if (values.tpMode === 'price') {
    if (!values.tpPrice || values.tpPrice <= 0) throw new Error('请输入有效止盈价')
    command.tp_price = values.tpPrice
  }
  if (Object.keys(command).length === Object.keys(base).length + 2) throw new Error('请选择一项保护调整')
  return [command]
}

export function canResume(operation: TradingOperation): boolean {
  return ['failed', 'partial_success'].includes(operation.status) &&
    ['reduce', 'full_close', 'adjust'].includes(String(operation.command?.operation)) &&
    operation.positions.some(child => child.status === 'failed' && child.retry_safe === true)
}

export interface LocalSubmission { requestId: string; command?: OperationCommand; originalId?: string; createdAt: string; observed?: boolean }
export const submissionKey = (tokenId: number) => `cfd-submissions:${tokenId}`
export function readSubmissions(tokenId: number): LocalSubmission[] {
  try { return JSON.parse(localStorage.getItem(submissionKey(tokenId)) || '[]') }
  catch { return [] }
}
export function rememberSubmissions(tokenId: number, added: LocalSubmission[]) {
  const all = readSubmissions(tokenId)
  const ids = new Set(all.map(item => item.requestId))
  localStorage.setItem(submissionKey(tokenId), JSON.stringify([...all, ...added.filter(item => !ids.has(item.requestId))].slice(-100)))
}
export function markSubmissionObserved(tokenId: number, requestId: string) {
  const items = readSubmissions(tokenId).map(item => item.requestId === requestId ? { ...item, observed: true } : item)
  localStorage.setItem(submissionKey(tokenId), JSON.stringify(items))
}
